import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { register, loginWithGoogle, UTMParams } from "@/services/auth";
import { toast } from "sonner";

const EmbedSignup = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [utmParams, setUtmParams] = useState<UTMParams>({});

  useEffect(() => {
    // Capture UTM parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const utm_source = urlParams.get("utm_source");
    const utm_medium = urlParams.get("utm_medium");
    const utm_campaign = urlParams.get("utm_campaign");
    const utm_content = urlParams.get("utm_content");
    const utm_term = urlParams.get("utm_term");

    const params: UTMParams = {};
    if (utm_source) params.utm_source = utm_source;
    if (utm_medium) params.utm_medium = utm_medium;
    if (utm_campaign) params.utm_campaign = utm_campaign;
    if (utm_content) params.utm_content = utm_content;
    if (utm_term) params.utm_term = utm_term;

    setUtmParams(params);

    // Store UTM params in sessionStorage for Google OAuth flow
    if (Object.keys(params).length > 0) {
      sessionStorage.setItem("utmParams", JSON.stringify(params));
    }
  }, []);

  const handleError = (error: string) => {
    toast.error(error);
  };

  const handleGoogleSignIn = async () => {
    if (isGoogleLoading) return;

    try {
      setIsGoogleLoading(true);

      const popup = window.open(
        "about:blank",
        "oauth",
        "height=600,width=800,status=yes,toolbar=no,menubar=no,location=no",
      );

      if (!popup) {
        throw new Error(
          "Failed to open the authentication window. Please allow popups for this site.",
        );
      }

      const popupCheckInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupCheckInterval);
          setIsGoogleLoading(false);
        }
      }, 3000);

      try {
        const authData = await loginWithGoogle(popup);
        clearInterval(popupCheckInterval);

        if (authData) {
          // Notify parent window of successful Google signup
          window.parent.postMessage(
            {
              type: "signup-success",
              email: authData.record.email,
              name: authData.record.name,
              provider: "google",
              utmParams,
            },
            "*",
          );

          toast.success("Account created successfully! Redirecting...");
        }
      } catch (err) {
        clearInterval(popupCheckInterval);
        throw err;
      }
    } catch (err: any) {
      handleError(err?.message || "Failed to sign in with Google");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (password !== confirmPassword) {
        toast.error("Passwords do not match");
        setIsLoading(false);
        return;
      }

      await register(name, email, password, confirmPassword, utmParams);

      // Notify parent window of successful signup
      window.parent.postMessage(
        {
          type: "signup-success",
          email,
          name,
          provider: "email",
          utmParams,
        },
        "*",
      );
    } catch (error) {
      console.error("Registration error:", error);
      handleError(
        error instanceof Error ? error.message : "Failed to create account",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4 max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Button
          variant="outline"
          type="button"
          className="w-full h-11"
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading}
        >
          <img src="/google.svg" alt="Google" className="w-5 h-5 mr-2" />
          {isGoogleLoading ? "Processing..." : "Continue with Google"}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-muted-foreground">
              OR CONTINUE WITH EMAIL
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            placeholder="John Doe"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <p className="text-xs text-muted-foreground">
            Must be at least 8 characters long
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <Input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Creating account..." : "Create account"}
        </Button>
      </form>
    </div>
  );
};

export default EmbedSignup;
